import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  tag?: string;
  breadcrumbs?: BreadcrumbItem[];
  action?: ReactNode;
  badge?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  subtitle,
  tag,
  breadcrumbs,
  action,
  badge,
  className,
}: PageHeaderProps) {
  return (
    <div className={cn('mb-6', className)}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb" className="mb-2 flex items-center gap-1.5 text-xs text-ink-3">
          {breadcrumbs.map((bc, idx) => {
            const isLast = idx === breadcrumbs.length - 1;
            return (
              <span key={idx} className="flex items-center gap-1.5">
                {idx > 0 && <span aria-hidden className="text-slate-300 dark:text-slate-600">/</span>}
                {isLast ? (
                  <span className="font-semibold text-ink dark:text-slate-200">{bc.label}</span>
                ) : bc.onClick ? (
                  <button
                    type="button"
                    onClick={bc.onClick}
                    className="hover:text-ink dark:hover:text-slate-200"
                  >
                    {bc.label}
                  </button>
                ) : bc.href ? (
                  <a href={bc.href} className="hover:text-ink dark:hover:text-slate-200">
                    {bc.label}
                  </a>
                ) : (
                  <span>{bc.label}</span>
                )}
              </span>
            );
          })}
        </nav>
      )}

      {tag && (
        <div className="mb-1 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-brand">
          {tag}
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="text-[22px] font-bold tracking-tight text-slate-800 dark:text-slate-100">
              {title}
            </h1>
            {badge}
          </div>
          {subtitle && (
            <p className="mt-1 text-[13px] text-ink-3 dark:text-slate-400">{subtitle}</p>
          )}
        </div>

        {action && <div className="shrink-0 sm:ms-4">{action}</div>}
      </div>
    </div>
  );
}

export default PageHeader;
